import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDocs, query, collection, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useSchoolId } from '../lib/auth/schoolContext';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';
import SchoolPageHeader from '../components/school/SchoolPageHeader';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities';

const OnePageProfilesPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { schoolId } = useSchoolId();
  const { can } = useSchoolCapabilities();

  useEffect(() => {
    if (!schoolId) return;

    let cancelled = false;
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        setError(null);

        const snap = await getDocs(
          query(collection(db, 'v2_one_page_profiles'), where('schoolId', '==', schoolId))
        );

        const rows = await Promise.all(
          snap.docs.map(async (profileDoc) => {
            const profileData = profileDoc.data();
            const studentId = profileData.studentId || profileDoc.id;
            let studentName = 'Unknown Student';
            try {
              const studentSnap = await getDoc(doc(db, 'students', studentId));
              if (studentSnap.exists()) {
                const studentData = studentSnap.data();
                studentName = `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || studentName;
              }
            } catch (nameErr) {
              console.log(`Could not fetch student name for ${studentId}:`, nameErr?.message);
            }

            return {
              id: profileDoc.id,
              studentId,
              studentName,
              headlineNeeds: profileData.headlineNeeds || [],
              topStrategies: profileData.topStrategies || [],
              generatedAt: profileData.generatedAt || null,
            };
          })
        );

        rows.sort((a, b) => {
          const dateA = a.generatedAt ? new Date(a.generatedAt).getTime() : 0;
          const dateB = b.generatedAt ? new Date(b.generatedAt).getTime() : 0;
          return dateB - dateA;
        });

        if (!cancelled) setProfiles(rows);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching one-page profiles:', err);
          setError('Failed to load one-page profiles. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfiles();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const formatDate = (value) => {
    if (!value) return 'Not yet synthesised';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not yet synthesised';
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading one-page profiles...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading One-Page Profiles</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8 space-y-4">
          <SchoolPageHeader
            title="One-Page Profiles"
            icon={Icons.BookOpen}
            breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'One-Page Profiles' }]}
            actions={[
              can(SCHOOL_ACTIONS.VIEW_STUDENTS) ? { label: 'Individual Support Plans', href: '/isps', variant: 'secondary' } : null,
            ].filter(Boolean)}
          />
        </div>

        {profiles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-blue-100 flex items-center justify-center">
              <Icons.BookOpen className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">No profiles yet</h2>
            <Link
              to="/isps"
              className="inline-flex items-center px-6 py-3 text-base font-medium rounded-xl shadow-md text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
            >
              Go to Individual Support Plans
            </Link>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-blue-200 transition-all overflow-hidden"
              >
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{profile.studentName}</h3>
                  <p className="text-xs text-gray-500 mt-1">Synthesised {formatDate(profile.generatedAt)}</p>
                  <p className="mt-3 text-sm text-gray-600 line-clamp-3">
                    {profile.topStrategies[0] || profile.headlineNeeds[0] || 'Not recorded'}
                  </p>
                </div>
                <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-100">
                  <Link
                    to={`/students/${profile.studentId}/one-page-profile`}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl text-blue-600 bg-blue-50 hover:bg-blue-100"
                  >
                    View Profile
                    <Icons.ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default OnePageProfilesPage;
